/**
 * TrustAuditUpdatesForm.jsx — Phase 12.5 · Subscribe-to-monthly-digest
 * capture on the Trust Center.
 *
 * Single goal: build a B2B audit-team email list by trading the
 * monthly "what changed in QorTrace's compliance posture" digest for
 * an email + (optional) firm + role.
 *
 * Behaves correctly under three states:
 *   1. Idle           — form rendered, awaiting input
 *   2. Submitting     — Turnstile + axios in-flight
 *   3. Subscribed     — confirmation card + "edit subscription" link
 *
 * Turnstile (Phase 12.1): rendered invisibly using the existing
 * REACT_APP_TURNSTILE_SITE_KEY. If the site key is unset, the form
 * still submits — backend fail-opens too.
 *
 * Renders inside the existing QorTrace dark-cyan theme on /trust.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Users, CheckCircle2, AlertOctagon } from "lucide-react";
import Turnstile from "react-turnstile";


const API = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";


export default function TrustAuditUpdatesForm() {
  const [email,  setEmail]  = useState("");
  const [firm,   setFirm]   = useState("");
  const [role,   setRole]   = useState("");
  const [tsToken, setTsToken] = useState("");
  const [count,   setCount]   = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState("");
  const [done,    setDone]    = useState(false);

  // Social-proof counter
  useEffect(() => {
    axios.get(`${API}/api/public/trust/audit-updates/count`)
         .then(r => setCount(r.data?.count ?? 0))
         .catch(() => setCount(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/api/public/trust/audit-updates/subscribe`, {
        email:           email.trim(),
        firm:            firm.trim() || undefined,
        role:            role.trim() || undefined,
        turnstile_token: tsToken,
      });
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      // Friendly mapping for the known backend errors.
      if (String(detail).includes("invalid_email")) {
        setErr("That doesn't look like a valid email address.");
      } else if (String(detail).startsWith("turnstile_failed")) {
        setErr("Bot-protection check failed — refresh the page and try again.");
      } else {
        setErr("Couldn't subscribe right now. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="mt-12" data-testid="trust-audit-subscribe-done">
        <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>
          MONTHLY AUDIT DIGEST
        </div>
        <div className="flex items-center gap-3 mt-2">
          <h2 className="text-2xl font-semibold" style={{ color: "#fff" }}>
            You're in.
          </h2>
          <span className="qt-mono text-[10px]"
                style={{
                  padding: "2px 7px", borderRadius: 2,
                  background: "#1FDD8322", color: "#1FDD83",
                  letterSpacing: "0.08em",
                }}>
            SUBSCRIBED
          </span>
        </div>
        <div className="mt-3 flex items-start gap-3 rounded-md p-4"
             style={{
               background: "rgba(31, 221, 131, 0.04)",
               border:     "1px solid rgba(31, 221, 131, 0.25)",
               borderLeft: "3px solid #1FDD83",
             }}>
          <CheckCircle2 size={18} style={{ color: "#1FDD83", marginTop: 2 }} />
          <div>
            <p className="text-sm" style={{ color: "var(--qt-text-1)" }}>
              Your first digest lands on the 1st of next month at 09:00 UTC.
              You can unsubscribe from any digest with one click.
            </p>
            <p className="text-xs mt-2" style={{ color: "var(--qt-text-3)" }}>
              Want to update your firm or role?{" "}
              <a href="mailto:trust@qortrace.com?subject=Audit%20digest%20update"
                 style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
                Email us
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-12" data-testid="trust-audit-subscribe-section">
      <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>
        MONTHLY AUDIT DIGEST
      </div>
      <div className="flex items-center gap-3 mt-2 mb-2 flex-wrap">
        <h2 className="text-2xl font-semibold" style={{ color: "#fff" }}>
          Get the monthly compliance posture digest
        </h2>
        {typeof count === "number" && count > 0 && (
          <span className="qt-mono text-[10px] flex items-center gap-1"
                data-testid="trust-audit-subscriber-count"
                style={{
                  padding: "2px 7px", borderRadius: 2,
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--qt-text-3)", letterSpacing: "0.08em",
                }}>
            <Users size={10} />
            {count.toLocaleString()} AUDITOR{count === 1 ? "" : "S"} SUBSCRIBED
          </span>
        )}
      </div>
      <p className="text-sm mt-2 mb-4" style={{ color: "var(--qt-text-3)" }}>
        One email per month. Surfaces every drift in our SPF/DKIM/DMARC posture,
        every subdomain takeover sweep, and every self-healing repair fired in
        the last 30 days. Nothing else. Built for audit teams that need a
        paper trail.
      </p>

      <form onSubmit={submit}
            data-testid="trust-audit-subscribe-form"
            className="rounded-md p-4"
            style={{
              background:  "rgba(255,255,255,0.02)",
              border:      "1px solid rgba(0,229,255,0.18)",
              borderLeft:  "3px solid var(--qt-cyan)",
            }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 relative">
            <Mail size={14}
                  style={{
                    position: "absolute", left: 12, top: "50%",
                    transform: "translateY(-50%)", color: "#7F8FA0",
                  }} />
            <input
              type="email"
              required
              placeholder="you@auditfirm.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="trust-audit-subscribe-email"
              style={inputStyle({ paddingLeft: 36 })}
            />
          </div>
          <input
            type="text"
            placeholder="Firm (optional)"
            value={firm}
            onChange={(e) => setFirm(e.target.value)}
            maxLength={120}
            data-testid="trust-audit-subscribe-firm"
            style={inputStyle()}
          />
          <input
            type="text"
            placeholder="Role (optional)"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            maxLength={120}
            data-testid="trust-audit-subscribe-role"
            style={inputStyle()}
          />
        </div>

        {TURNSTILE_SITE_KEY && (
          <div className="mt-3" data-testid="trust-audit-subscribe-turnstile">
            <Turnstile
              sitekey={TURNSTILE_SITE_KEY}
              theme="dark"
              appearance="interaction-only"
              onVerify={setTsToken}
              onError={() => setTsToken("")}
              onExpire={() => setTsToken("")}
            />
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button type="submit"
                  disabled={busy || !email}
                  data-testid="trust-audit-subscribe-submit"
                  style={{
                    padding: "9px 18px", borderRadius: 4,
                    background: busy ? "#1F2937" : "var(--qt-cyan)",
                    color: "#0B1220", fontFamily: "'IBM Plex Sans', sans-serif",
                    fontWeight: 600, fontSize: 13,
                    border: "none",
                    cursor: busy ? "not-allowed" : "pointer",
                    letterSpacing: "0.01em",
                  }}>
            {busy ? "Subscribing…" : "Subscribe to the digest"}
          </button>
          <span className="text-xs"
                style={{ color: "var(--qt-text-3)" }}>
            Unsubscribe in one click from any email.
          </span>
        </div>

        {err && (
          <div className="mt-3 flex items-center gap-2 text-xs"
               data-testid="trust-audit-subscribe-error"
               style={{ color: "#FF6E6E" }}>
            <AlertOctagon size={12} /> {err}
          </div>
        )}
      </form>
    </section>
  );
}


function inputStyle(extra = {}) {
  return {
    width: "100%",
    padding: "9px 12px",
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: 13,
    background: "rgba(11, 18, 32, 0.6)",
    border: "1px solid #1F2937",
    color: "#E2E8F0",
    borderRadius: 4,
    outline: "none",
    ...extra,
  };
}
